/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AppPhase, DealingSubPhase } from '../../models/communication/app-enums.model';
import { TableUpdate } from '../../models/communication/app-state.model';
import { GameBase } from '../game-base';
import { GameFlowBase } from '../game-flow-base';
import { CardState, SideState } from '../models/game-view.model';

export class OpenGameFlow extends GameFlowBase<GameBase> {
    private static readonly sideSelectAnimationIntervalMs: number = 330;

    private animationInterval?: ReturnType<typeof setInterval>;

    override update(tableUpdate?: TableUpdate, appPhase?: AppPhase, dealingSubPhase?: DealingSubPhase): void {
        if (appPhase === AppPhase.Decision) {
            this.game.showPlayerDecisionTime();
            return;
        }

        if (!this.game.isUpdateDataValid(tableUpdate)) {
            return;
        }

        // Hack: should've come from the backend
        if (dealingSubPhase === undefined) {
            if (this.game.getCards().every((card) => card.state !== CardState.Show)) {
                // eslint-disable-next-line no-param-reassign
                dealingSubPhase = DealingSubPhase.FaceDown;
            } else {
                // eslint-disable-next-line no-param-reassign
                dealingSubPhase = DealingSubPhase.Dealing;
            }
        }

        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (dealingSubPhase) {
            case DealingSubPhase.FaceDown: {
                if ('cardPosition' in tableUpdate) {
                    this.game.updateCard(tableUpdate.cardPosition!, CardState.Checked);
                }

                if ('nextCardPosition' in tableUpdate) {
                    this.game.showFaceDownMessage(tableUpdate.nextCardPosition!);
                } else {
                    this.game.showAllCardsDrawn();
                }

                break;
            }

            case DealingSubPhase.SideSelectionAnimation: {
                this.game.updateVisibleCards(CardState.Back);
                this.startSideSelectAnimation();
                break;
            }

            case DealingSubPhase.LeftSide:
            case DealingSubPhase.RightSide: {
                if (this.isSideSelectionAnimationActive()) {
                    this.stopSideSelectAnimation();
                } else if ('cardPosition' in tableUpdate) {
                    this.game.revealCard(tableUpdate.cardPosition!, tableUpdate.card!);
                    this.game.showHandValue(tableUpdate);
                }
                break;
            }

            case DealingSubPhase.Dealing: {
                if ('cardPosition' in tableUpdate) {
                    this.game.revealCard(tableUpdate.cardPosition!, tableUpdate.card!);
                    this.game.showHandValue(tableUpdate);
                }

                if ('nextCardPosition' in tableUpdate) {
                    this.game.showFaceUpMessage(tableUpdate.nextCardPosition!);
                }

                if ('outcome' in tableUpdate) {
                    this.game.handleOutcome(tableUpdate);
                }
                break;
            }
        }
    }

    override destroy(): void {
        this.stopSideSelectAnimation();
    }

    startSideSelectAnimation(): void {
        this.game.updateTitle($localize`Side selection time`, { isSpecial: true });

        // A replayed update must not start a second interval: only the first one would ever be cleared
        if (!this.isSideSelectionAnimationActive()) {
            this.animationInterval = this.setSideSelectInterval();
        }
    }

    stopSideSelectAnimation(): void {
        clearInterval(this.animationInterval);
        this.animationInterval = undefined;
    }

    isSideSelectionAnimationActive(): boolean {
        return Boolean(this.animationInterval);
    }

    setSideSelectInterval(): ReturnType<typeof setInterval> {
        let sideIndex = 0;
        const { length } = this.game.view.sides;

        const animate = (): void => {
            this.game.view.sides[sideIndex].state = SideState.Current;
            sideIndex = (sideIndex + 1) % length;
            this.game.view.sides[sideIndex].state = SideState.Muted;
        };

        animate();
        return setInterval(animate, OpenGameFlow.sideSelectAnimationIntervalMs);
    }
}
